// src/pages/dashboard/AdminSubscriptions.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  Users, Search, Loader2, X, Plus, Pencil, Trash2, FastForward, Ban, RefreshCw,
  CheckCircle2, Clock, XCircle, AlertCircle, Gift, CreditCard, UserPlus, ChevronDown,
  Mail, Phone, UserCheck, Power, Globe, Star, Utensils, Contact, Nfc, Save,
  User as UserIcon, ArrowLeftRight,
} from 'lucide-react'
import {
  getAdminSubscriptions,
  createAdminSubscription,
  updateAdminSubscription,
  deleteAdminSubscription,
  extendAdminSubscription,
  cancelAdminSubscription,
  createAdminClient,
  updateAdminClient,
  deleteAdminClient,
  getPlans,
  setClientActive,
  getClientPages,
  updateClientPage,
  getAssignablePages,
  assignPageToClient,
  unassignPage,
} from '../../api/subscription'
import { cn } from '../../lib/utils'

/* ---------------- Helpers ---------------- */

const PAYMENT_META = {
  paid: { label: 'Payé', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  trialing: { label: 'Essai', color: 'bg-sky-50 text-sky-700 border-sky-200', icon: Gift },
  cancelled: { label: 'Annulé', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  grace_period: { label: 'Grâce', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  expired: { label: 'Expiré', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  unpaid: { label: 'Non payé', color: 'bg-slate-50 text-slate-600 border-slate-200', icon: CreditCard },
}

const PAGE_TYPES = {
  vcard: { label: 'Carte de visite', icon: Contact },
  bio: { label: 'Bio / Liens', icon: UserIcon },
  menu: { label: 'Menu', icon: Utensils },
  review: { label: 'Avis Google', icon: Star },
  nfc: { label: 'Carte NFC', icon: Nfc },
}

const PaymentBadge = ({ status }) => {
  const meta = PAYMENT_META[status] || PAYMENT_META.unpaid
  const Icon = meta.icon
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border', meta.color)}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  )
}

const FILTERS = [
  { id: '', label: 'Tous' },
  { id: 'paid', label: 'Payés' },
  { id: 'trialing', label: 'Essai' },
  { id: 'cancelled', label: 'Annulés' },
  { id: 'expired', label: 'Expirés' },
  { id: 'unpaid', label: 'Non payés' },
]

const toInputDate = (iso) => (iso ? String(iso).slice(0, 10) : '')
const fromInputDate = (d) => new Date(`${d}T23:59:59`).toISOString()
const inDays = (n) => toInputDate(new Date(Date.now() + n * 86400000).toISOString())
const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?'
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—')
const endOf = (u) => u.subscription?.ends_at || u.subscription?.current_period_end
const isSuspended = (u) => u?.status === 'suspended'

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#006b5f]/30'

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[#000a1e]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
)

const Avatar = ({ name, className }) => (
  <div className={cn('rounded-full bg-[#eff2ff] text-[#006b5f] font-bold flex items-center justify-center shrink-0', className)}>
    {initials(name)}
  </div>
)

const ModalShell = ({ onClose, maxW = 'max-w-md', children }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
    <div onClick={onClose} className="absolute inset-0 bg-[#000a1e]/60 backdrop-blur-sm" />
    <div className={cn('relative w-full max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-7 sm:p-8', maxW)}>
      <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
        <X className="w-4 h-4" />
      </button>
      {children}
    </div>
  </div>
)

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

/* ---------------- Client picker ---------------- */

function ClientPicker({ selected, onPick, onClear }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)
  const dq = useDebounced(q)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    getAdminSubscriptions({ search: dq })
      .then((res) => {
        if (cancelled) return
        const rows = res.data?.data || res.data || []
        setResults(Array.isArray(rows) ? rows.filter((u) => u.role !== 'admin') : [])
      })
      .catch(() => !cancelled && setResults([]))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [dq, open])

  useEffect(() => {
    const h = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  if (selected) {
    return (
      <div className="flex items-center gap-3 p-3 bg-[#eff2ff] rounded-2xl">
        <Avatar name={selected.name} className="w-11 h-11 text-sm" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-[#000a1e] truncate">{selected.name}</p>
          <p className="text-xs text-slate-500 truncate">
            {selected.email}{selected.phone ? ` · ${selected.phone}` : ''}
          </p>
        </div>
        <button type="button" onClick={onClear} className="text-xs font-semibold text-[#006b5f] hover:underline">
          Changer
        </button>
      </div>
    )
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm text-left"
      >
        <UserPlus className="w-4 h-4 text-[#006b5f]" />
        <span className="flex-1 text-slate-600">Nouveau client — ou choisir un client existant…</span>
        <ChevronDown className={cn('w-4 h-4 text-slate-400 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-10 mt-2 w-full bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
          <div className="relative p-2 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, email ou téléphone…"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-[#f9f9ff] text-sm focus:outline-none"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading && (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#006b5f] animate-spin" /></div>
            )}
            {!loading && results.length === 0 && (
              <p className="py-6 text-center text-xs text-slate-400">Aucun client trouvé</p>
            )}
            {!loading && results.map((u) => (
              <button
                type="button"
                key={u.id}
                onClick={() => { onPick(u); setOpen(false); setQ('') }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
              >
                <Avatar name={u.name} className="w-9 h-9 text-xs" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#000a1e] truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-2 truncate">
                    <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{u.email}</span>
                    {u.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</span>}
                  </p>
                </div>
                <PaymentBadge status={u.payment_status} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------- Pages du client + attribution ---------------- */

function unwrapRows(response) {
  const rows =
    response?.data?.data ??
    response?.data ??
    response?.pages ??
    response

  return Array.isArray(rows) ? rows : []
}

function ClientPages({ userId }) {
  const [pages, setPages] = useState([])
  const [assignablePages, setAssignablePages] = useState([])
  const [selectedPageId, setSelectedPageId] = useState('')
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const [unassigningId, setUnassigningId] = useState(null)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')

    const [clientResult, availableResult] = await Promise.allSettled([
      getClientPages(userId),
      getAssignablePages(),
    ])

    if (clientResult.status === 'fulfilled') {
      setPages(unwrapRows(clientResult.value))
    } else {
      setPages([])
      setError(clientResult.reason?.message || 'Impossible de charger les pages du client.')
    }

    if (availableResult.status === 'fulfilled') {
      setAssignablePages(unwrapRows(availableResult.value))
    } else {
      setAssignablePages([])
      setError((old) => old || availableResult.reason?.message || 'Impossible de charger les pages disponibles.')
    }

    setLoading(false)
  }, [userId])

  useEffect(() => { load() }, [load])

  const assign = async () => {
    if (!selectedPageId) return

    setAssigning(true)
    setError('')
    setSuccess('')

    try {
      await assignPageToClient(userId, selectedPageId)
      setSelectedPageId('')
      setSuccess('La page a été attribuée au client.')
      await load()
    } catch (e) {
      setError(e?.message || "Impossible d'attribuer cette page.")
    } finally {
      setAssigning(false)
    }
  }

  const unassign = async (pageId) => {
    if (!window.confirm("Rendre cette page au compte administrateur ?")) return

    setUnassigningId(pageId)
    setError('')
    setSuccess('')

    try {
      await unassignPage(pageId)
      setSuccess('La page a été rendue au compte administrateur.')
      await load()
    } catch (e) {
      setError(e?.message || "Impossible de récupérer cette page.")
    } finally {
      setUnassigningId(null)
    }
  }

  const upd = (k) => (e) =>
    setEditing((p) => ({
      ...p,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }))

  const save = async () => {
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const { id, title, display_name, job_title, company, bio, slug, is_published } = editing
      await updateClientPage(id, {
        title,
        display_name,
        job_title,
        company,
        bio,
        slug,
        is_published,
      })
      setEditing(null)
      setSuccess('La page a été mise à jour.')
      await load()
    } catch (e) {
      setError(e?.message || 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  if (editing) {
    const T = PAGE_TYPES[editing.type] || { label: editing.type, icon: Globe }

    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => { setEditing(null); setError('') }}
          className="text-xs font-semibold text-[#006b5f] hover:underline"
        >
          ← Retour aux pages
        </button>

        <p className="font-semibold text-sm flex items-center gap-2">
          <T.icon className="w-4 h-4 text-[#006b5f]" /> {T.label}
        </p>

        {error && <div className="p-3 bg-red-50 text-red-600 text-xs rounded-xl">{error}</div>}

        <Field label="Titre">
          <input value={editing.title || ''} onChange={upd('title')} className={inputCls} />
        </Field>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nom affiché">
            <input value={editing.display_name || ''} onChange={upd('display_name')} className={inputCls} />
          </Field>
          <Field label="Slug">
            <input value={editing.slug || ''} onChange={upd('slug')} className={inputCls} />
          </Field>
          <Field label="Poste">
            <input value={editing.job_title || ''} onChange={upd('job_title')} className={inputCls} />
          </Field>
          <Field label="Entreprise">
            <input value={editing.company || ''} onChange={upd('company')} className={inputCls} />
          </Field>
        </div>

        <Field label="Bio">
          <textarea rows={3} value={editing.bio || ''} onChange={upd('bio')} className={inputCls} />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!editing.is_published} onChange={upd('is_published')} />
          Publiée
        </label>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="w-full py-3 rounded-xl bg-[#006b5f] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer la page
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs rounded-xl">
          {success}
        </div>
      )}

      {/* Attribuer une page créée par l'admin */}
      <div className="p-4 rounded-2xl border border-[#006b5f]/20 bg-[#f5fbfa] space-y-3">
        <div>
          <p className="font-semibold text-sm text-[#000a1e] flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-[#006b5f]" />
            Attribuer une page existante
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Choisissez une page appartenant au compte administrateur pour l'attribuer à ce client.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedPageId}
            onChange={(e) => setSelectedPageId(e.target.value)}
            className={`${inputCls} flex-1`}
          >
            <option value="">Choisir une page à attribuer…</option>

            {assignablePages
              .filter(
                (p) =>
                  !p.assigned_user_id ||
                  Number(p.assigned_user_id) === Number(userId)
              )
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || p.display_name || p.slug} — {PAGE_TYPES[p.type]?.label || p.type}
                  {p.assigned_user_id ? ' (Déjà attribuée à ce client)' : ''}
                </option>
              ))}
          </select>

          <button
            type="button"
            onClick={assign}
            disabled={!selectedPageId || assigning}
            className="px-4 py-2.5 rounded-xl bg-[#006b5f] text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap"
          >
            {assigning && <Loader2 className="w-4 h-4 animate-spin" />}
            Attribuer au client
          </button>
        </div>

        {!assignablePages.some(
          (p) =>
            !p.assigned_user_id ||
            Number(p.assigned_user_id) === Number(userId)
        ) && (
          <p className="text-xs text-slate-400">
            Aucune page disponible dans le compte administrateur.
          </p>
        )}
      </div>

      {/* Pages du client */}
      <div>
        <p className="font-semibold text-sm text-[#000a1e] mb-2">Pages de ce client</p>

        {!pages.length ? (
          <p className="py-8 text-center text-sm text-slate-400">
            Ce client n'a encore aucune page.
          </p>
        ) : (
          <div className="space-y-2">
            {pages.map((p) => {
              const T = PAGE_TYPES[p.type] || { label: p.type, icon: Globe }
              const assignedByAdmin = !!p.assigned_user_id

              return (
                <div
                  key={p.id}
                  className="flex flex-wrap items-center gap-3 p-3 rounded-2xl border border-slate-100"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#eff2ff] text-[#006b5f] flex items-center justify-center shrink-0">
                    <T.icon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {p.title || p.display_name || p.slug}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {T.label} · /{p.slug} · {p.is_published ? 'Publiée' : 'Brouillon'}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      Propriétaire : {p.user?.name || 'Admin'}
                      {p.assignedUser?.name
                        ? ` · Client : ${p.assignedUser.name}`
                        : ''}
                    </p>
                  </div>

                  <a
                    href={`/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-slate-500 hover:underline"
                  >
                    Voir
                  </a>

                  <button
                    type="button"
                    onClick={() => setEditing({ ...p })}
                    title="Éditer"
                    className="p-2 rounded-xl bg-sky-50 text-sky-700"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>

                  {assignedByAdmin && (
                    <button
                      type="button"
                      onClick={() => unassign(p.id)}
                      disabled={unassigningId === p.id}
                      title="Rendre à l'administrateur"
                      className="px-3 py-2 rounded-xl bg-amber-50 text-amber-700 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {unassigningId === p.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <ArrowLeftRight className="w-3.5 h-3.5" />}
                      Rendre à l'admin
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/* ---------------- Client modal ---------------- */

function ClientModal({ user: initialUser, plans, onClose, onSuccess }) {
  const [picked, setPicked] = useState(null)
  const [tab, setTab] = useState('infos')
  const user = initialUser || picked
  const isEdit = !!user
  const sub = user?.subscription

  const buildForm = (u) => ({
    name: u?.name || '',
    email: u?.email || '',
    phone: u?.phone || '',
    password: '',
    plan_id: u?.plan?.id || (u ? '' : plans[0]?.id || ''),
    status: u?.subscription?.status || 'active',
    end_date: toInputDate(u?.subscription?.current_period_end) || inDays(30),
  })

  const [form, setForm] = useState(buildForm(initialUser))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const pick = (u) => { setPicked(u); setForm(buildForm(u)); setError('') }
  const clear = () => { setPicked(null); setForm(buildForm(null)); setError(''); setTab('infos') }

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const subPayload = form.plan_id
        ? { plan_id: form.plan_id, status: form.status, current_period_end: fromInputDate(form.end_date) }
        : {}

      if (!isEdit) {
        await createAdminClient({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          password: form.password,
          ...subPayload,
        })
      } else {
        await updateAdminClient(user.id, {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          password: form.password || undefined,
        })

        if (form.plan_id) {
          if (sub?.id) await updateAdminSubscription(sub.id, subPayload)
          else await createAdminSubscription({ user_id: user.id, ...subPayload })
        }
      }

      onSuccess()
    } catch (err) {
      setError(err?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  const removeSubscription = async () => {
    if (!sub?.id || !window.confirm("Retirer l'abonnement ? Le client repassera en gratuit.")) return
    setLoading(true)

    try {
      await deleteAdminSubscription(sub.id)
      onSuccess()
    } catch (err) {
      setError(err?.message || 'Erreur')
      setLoading(false)
    }
  }

  return (
    <ModalShell onClose={onClose} maxW="max-w-xl">
      <div className="space-y-4">
        <div>
          <h3 className="font-display font-bold text-xl text-[#000a1e] flex items-center gap-2">
            {isEdit ? `Modifier ${user.name}` : 'Nouveau client payant'}
            {isEdit && isSuspended(user) && (
              <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-white">Suspendu</span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {isEdit ? 'Infos du client, abonnement et pages.' : 'Créez un compte, ou sélectionnez un client existant.'}
          </p>
        </div>

        {isEdit && (
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            {[['infos', 'Infos & abonnement'], ['pages', 'Pages du client']].map(([id, label]) => (
              <button
                type="button"
                key={id}
                onClick={() => setTab(id)}
                className={cn(
                  'flex-1 py-2 rounded-lg text-xs font-semibold transition',
                  tab === id ? 'bg-white shadow text-[#006b5f]' : 'text-slate-500'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {isEdit && tab === 'pages' ? (
          <>
            <ClientPages userId={user.id} />
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 rounded-xl border border-slate-200 font-semibold text-sm"
            >
              Fermer
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {!initialUser && <ClientPicker selected={picked} onPick={pick} onClear={clear} />}

            {error && (
              <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span className="break-words">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nom">
                <input required value={form.name} onChange={set('name')} className={inputCls} />
              </Field>
              <Field label="Téléphone">
                <input value={form.phone} onChange={set('phone')} className={inputCls} />
              </Field>
            </div>

            <Field label="Email">
              <input required type="email" value={form.email} onChange={set('email')} className={inputCls} />
            </Field>

            <Field label={isEdit ? 'Nouveau mot de passe (vide = inchangé)' : 'Mot de passe (8 caractères min.)'}>
              <input
                type="text"
                required={!isEdit}
                minLength={isEdit ? undefined : 8}
                value={form.password}
                onChange={set('password')}
                className={inputCls}
                autoComplete="new-password"
              />
            </Field>

            <div className="pt-4 border-t border-slate-100">
              <p className="text-[10px] font-bold uppercase text-slate-400 mb-3">Abonnement</p>
              <div className="space-y-3">
                <Field label="Plan">
                  <select value={form.plan_id} onChange={set('plan_id')} className={inputCls}>
                    <option value="">— Aucun (gratuit) —</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} — {p.price} {p.currency}</option>
                    ))}
                  </select>
                </Field>

                {form.plan_id && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Statut">
                      <select value={form.status} onChange={set('status')} className={inputCls}>
                        <option value="active">Actif (payé)</option>
                        <option value="trialing">En essai</option>
                        <option value="past_due">Impayé (grâce)</option>
                        <option value="canceled">Annulé</option>
                        <option value="expired">Expiré</option>
                      </select>
                    </Field>
                    <Field label="Fin de période">
                      <input required type="date" value={form.end_date} onChange={set('end_date')} className={inputCls} />
                    </Field>
                  </div>
                )}
              </div>

              {isEdit && sub?.id && (
                <button
                  type="button"
                  onClick={removeSubscription}
                  className="mt-3 text-xs font-semibold text-red-600 hover:underline inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Retirer l'abonnement (repasser en gratuit)
                </button>
              )}
            </div>

            <div className="pt-2 flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-sm">
                Fermer
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-[#006b5f] text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalShell>
  )
}

/* ---------------- Subscribe existing client ---------------- */

function SubscribeModal({ user, plans, onClose, onSuccess }) {
  const [planId, setPlanId] = useState(plans[0]?.id || '')
  const [status, setStatus] = useState('active')
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await createAdminSubscription({
        user_id: user.id,
        plan_id: planId,
        status,
        current_period_end: new Date(Date.now() + days * 86400000).toISOString(),
      })
      onSuccess()
    } catch (err) {
      setError(err?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalShell onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <h3 className="font-display font-bold text-xl text-[#000a1e]">Abonner un client</h3>

        <div className="flex items-center gap-3 p-3 bg-[#eff2ff] rounded-2xl">
          <Avatar name={user.name} className="w-11 h-11 text-sm" />
          <div className="min-w-0">
            <p className="font-semibold text-sm text-[#000a1e] truncate">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}{user.phone ? ` · ${user.phone}` : ''}</p>
          </div>
        </div>

        {error && <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl">{error}</div>}

        <Field label="Plan">
          <select value={planId} onChange={(e) => setPlanId(e.target.value)} className={inputCls}>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.price} {p.currency}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Statut">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
              <option value="active">Actif (payé)</option>
              <option value="trialing">En essai</option>
              <option value="past_due">Impayé (grâce)</option>
            </select>
          </Field>
          <Field label="Durée (jours)">
            <input type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value))} className={inputCls} />
          </Field>
        </div>

        <div className="pt-2 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-sm">Fermer</button>
          <button
            type="submit"
            disabled={loading || !planId}
            className="flex-1 py-3 rounded-xl bg-[#006b5f] text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Créer
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

function ExtendModal({ onClose, onConfirm }) {
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setLoading(true)
    setError('')
    try {
      await onConfirm(days)
    } catch (e) {
      setError(e?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalShell onClose={onClose} maxW="max-w-sm">
      <h3 className="font-display font-bold text-xl text-[#000a1e] mb-4">Prolonger l'abonnement</h3>
      {error && <div className="p-3 mb-3 bg-red-50 text-red-600 text-xs rounded-xl">{error}</div>}
      <Field label="Nombre de jours à ajouter">
        <input type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value))} className={inputCls} />
      </Field>
      <div className="mt-6 flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-sm">Fermer</button>
        <button
          onClick={submit}
          disabled={loading || days < 1}
          className="flex-1 py-3 rounded-xl bg-[#006b5f] text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Prolonger
        </button>
      </div>
    </ModalShell>
  )
}

function ConfirmModal({ title, message, danger = true, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setLoading(true)
    setError('')
    try {
      await onConfirm()
    } catch (e) {
      setError(e?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalShell onClose={onClose} maxW="max-w-sm">
      <h3 className="font-display font-bold text-xl text-[#000a1e] mb-2">{title}</h3>
      <p className="text-sm text-slate-600">{message}</p>
      {error && <div className="p-3 mt-3 bg-red-50 text-red-600 text-xs rounded-xl">{error}</div>}
      <div className="mt-6 flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-sm">Fermer</button>
        <button
          onClick={submit}
          disabled={loading}
          className={cn(
            'flex-1 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2',
            danger ? 'bg-red-600' : 'bg-[#006b5f]'
          )}
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Confirmer
        </button>
      </div>
    </ModalShell>
  )
}

/* ---------------- Page ---------------- */

export default function AdminSubscriptions() {
  const [users, setUsers] = useState([])
  const [stats, setStats] = useState(null)
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('')

  const [subscribeModal, setSubscribeModal] = useState(null)
  const [clientModal, setClientModal] = useState(null)
  const [extendModal, setExtendModal] = useState(null)
  const [cancelModal, setCancelModal] = useState(null)
  const [deleteModal, setDeleteModal] = useState(null)
  const [suspendModal, setSuspendModal] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const res = await getAdminSubscriptions({ search, payment_status: filter })
      const rows = res.data?.data || res.data || []
      setUsers(Array.isArray(rows) ? rows : [])
      setStats(res.stats || null)
    } catch (err) {
      console.error(err)
      setError(err?.message || 'Impossible de charger les clients.')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [search, filter])

  useEffect(() => {
    const t = setTimeout(load, 200)
    return () => clearTimeout(t)
  }, [load])

  useEffect(() => {
    getPlans().then((r) => setPlans(r.data)).catch(() => {})
  }, [])

  const afterChange = (close) => () => {
    close()
    load()
  }

  return (
    <div className="min-h-screen bg-[#f9f9ff]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-3xl text-[#000a1e] flex items-center gap-2">
              <Users className="w-7 h-7 text-[#006b5f]" /> Clients
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gérez les clients, leurs abonnements et leurs pages.</p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, email, téléphone…"
                className="pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm w-full sm:w-72"
              />
            </div>

            <button onClick={load} className="p-2.5 rounded-xl border border-slate-200 bg-white" title="Recharger">
              <RefreshCw className={cn('w-4 h-4 text-slate-500', loading && 'animate-spin')} />
            </button>

            <button
              onClick={() => setClientModal({ user: null })}
              className="px-4 py-2.5 rounded-xl bg-[#006b5f] text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" /> Nouveau client
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Total clients', value: stats.total_users, color: 'text-[#000a1e]', icon: Users },
              { label: 'Payants', value: stats.paid_users, color: 'text-emerald-600', icon: UserCheck },
              { label: 'Gratuits', value: stats.free_users, color: 'text-slate-500', icon: Gift },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                <p className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
                  <s.icon className="w-3 h-3" /> {s.label}
                </p>
                <p className={cn('font-display font-bold text-2xl mt-1', s.color)}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 p-2 flex gap-1 overflow-x-auto mb-6 shadow-sm">
          {FILTERS.map((f) => (
            <button
              key={f.id || 'all'}
              onClick={() => setFilter(f.id)}
              className={cn(
                'px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition',
                filter === f.id ? 'bg-[#006b5f] text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
          </div>
        )}

        {error && (
          <div className="max-w-lg mx-auto p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-600 text-center flex items-center gap-2 justify-center">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {!loading && !error && users.length === 0 && (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun client.</p>
          </div>
        )}

        {!loading && users.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto shadow-sm">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-[10px] font-bold uppercase text-slate-400">
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3 hidden md:table-cell">Plan</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Fin période</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setClientModal({ user: u })}
                    className={cn('border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer', isSuspended(u) && 'opacity-60')}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={u.name} className="w-9 h-9 text-xs" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#000a1e] truncate max-w-[200px]">{u.name}</p>
                          <p className="text-xs text-slate-500 truncate max-w-[200px]">{u.email}</p>
                          {u.phone && <p className="text-[10px] text-slate-400">{u.phone}</p>}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-sm text-[#000a1e]">{u.plan?.name || '—'}</p>
                      {u.plan && <p className="text-xs text-slate-500">{u.plan.price} {u.plan.currency}</p>}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <PaymentBadge status={u.payment_status} />
                        {isSuspended(u) && (
                          <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-white">Suspendu</span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{fmtDate(endOf(u))}</td>

                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1.5 whitespace-nowrap">
                        <button
                          onClick={() => setClientModal({ user: u })}
                          title="Modifier / voir les pages"
                          className="p-2 rounded-xl bg-sky-50 text-sky-700"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>

                        {!u.subscription && (
                          <button
                            onClick={() => setSubscribeModal({ user: u })}
                            title="Abonner"
                            className="p-2 rounded-xl bg-emerald-50 text-emerald-700"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {u.subscription && (
                          <>
                            <button
                              onClick={() => setExtendModal({ subId: u.subscription.id })}
                              title="Prolonger"
                              className="p-2 rounded-xl bg-slate-100 text-[#000a1e]"
                            >
                              <FastForward className="w-3.5 h-3.5" />
                            </button>

                            <button
                              onClick={() => setCancelModal({ subId: u.subscription.id })}
                              title="Annuler à la fin de la période"
                              className="p-2 rounded-xl bg-amber-50 text-amber-700"
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}

                        {u.role !== 'admin' && (
                          <button
                            onClick={() => setSuspendModal({ user: u })}
                            title={isSuspended(u) ? 'Réactiver le compte' : 'Désactiver le compte'}
                            className={cn(
                              'p-2 rounded-xl',
                              isSuspended(u) ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'
                            )}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </button>
                        )}

                        {u.role !== 'admin' && (
                          <button
                            onClick={() => setDeleteModal({ user: u })}
                            title="Supprimer le client"
                            className="p-2 rounded-xl bg-red-50 text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {subscribeModal && (
        <SubscribeModal
          user={subscribeModal.user}
          plans={plans}
          onClose={() => setSubscribeModal(null)}
          onSuccess={afterChange(() => setSubscribeModal(null))}
        />
      )}

      {clientModal && (
        <ClientModal
          user={clientModal.user}
          plans={plans}
          onClose={() => setClientModal(null)}
          onSuccess={afterChange(() => setClientModal(null))}
        />
      )}

      {extendModal && (
        <ExtendModal
          onClose={() => setExtendModal(null)}
          onConfirm={async (days) => {
            await extendAdminSubscription(extendModal.subId, days)
            setExtendModal(null)
            load()
          }}
        />
      )}

      {cancelModal && (
        <ConfirmModal
          title="Annuler cet abonnement ?"
          message="L'accès restera actif jusqu'à la fin de la période."
          onClose={() => setCancelModal(null)}
          onConfirm={async () => {
            await cancelAdminSubscription(cancelModal.subId)
            setCancelModal(null)
            load()
          }}
        />
      )}

      {suspendModal && (() => {
        const willSuspend = !isSuspended(suspendModal.user)

        return (
          <ConfirmModal
            danger={willSuspend}
            title={willSuspend ? `Désactiver ${suspendModal.user.name} ?` : `Réactiver ${suspendModal.user.name} ?`}
            message={willSuspend
              ? 'Le client sera déconnecté, ne pourra plus se connecter et ses pages publiques seront masquées. Ses données sont conservées.'
              : "Le client retrouvera l'accès à son compte et ses pages redeviendront visibles."}
            onClose={() => setSuspendModal(null)}
            onConfirm={async () => {
              await setClientActive(suspendModal.user.id, !willSuspend)
              setSuspendModal(null)
              load()
            }}
          />
        )
      })()}

      {deleteModal && (
        <ConfirmModal
          title={`Supprimer ${deleteModal.user.name} ?`}
          message="Le client sera supprimé (suppression logique) ainsi que ses abonnements."
          onClose={() => setDeleteModal(null)}
          onConfirm={async () => {
            await deleteAdminClient(deleteModal.user.id)
            setDeleteModal(null)
            load()
          }}
        />
      )}
    </div>
  )
}
